import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';

import { i18n, loadLanguageAsync } from '@/i18n';

// Directly import Home view for faster rendering of first page
import accessmasterManager from '@/services/Security/accessmasterManager';
import loginService from '@/services/Security/loginService';
import userInfoService from '@/services/Security/UserInfo/userInfoService';
import projectRoute from '@/config/router/project';
import importRoute from '@/config/router/imports';
import securityRoute from '@/config/router/security';
import paymentRoute from '@/config/router/payment';
import patientRoute from '@/config/router/patient';
import dataLineRoute from '@/config/router/dataLine';
import exportRoute from '@/config/router/export';
import patientExperienceRoute from '@/config/router/patientExperience';
import commonRoute from '@/config/router/common';
import undueRoute from '@/config/router/undue';
import patientExperienceLoginService from '@/services/Security/patientExperienceLoginService';
import { RouteConfig } from '@/types';
import notificationManager from '@/services/Notification/NotificationManager';
import projectGroupRoute from '@/config/router/projectGroup';
import surveyCampaignRoute from './surveyCampaign';

// Env var
declare const MAINTENANCE: string;

Vue.use(Router);
Vue.use(Meta, {
	refreshOnceOnNavigation: true
});

const allRoutes = commonRoute.concat(
	projectRoute,
	paymentRoute,
	patientRoute,
	dataLineRoute,
	importRoute,
	projectGroupRoute,
	exportRoute,
	patientExperienceRoute,
	securityRoute,
	surveyCampaignRoute,
	undueRoute
);

translateRoute(allRoutes);

/** See https://router.vuejs.org/fr/essentials/navigation.html for help */
const router = new Router({
	base: '/v2/',
	mode: 'history', // Use the History API, and remove the hash from the URL
	routes: allRoutes,
	// Native-like scroll behavior
	scrollBehavior(to, from, savedPosition) {
		// If there is an hash, this is an anchor, so scroll to it
		if (to.hash) {
			return {
				selector: to.hash
			};
		} else {
			// Else simulate browser behavior by returning to the previous position, or fallback to top
			if (savedPosition) {
				return savedPosition;
			} else {
				return { x: 0, y: 0 };
			}
		}
	}
});

/** Resolve translations before each navigation */
export function loadLang() {
	const lang = i18n.locale;

	return loadLanguageAsync(lang);
}

router.beforeEach((to, from, next) => {

	notificationManager.clearNotification();

	// If maintenance is enabled
	if ('true' === MAINTENANCE) {
		// Avoid infinite loop
		if ('maintenance' !== to.name) {
			// Redirect to maintenance page
			loadLang().then(() => next({ name: 'maintenance' }));
		} else {
			// Else, resolve route
			loadLang().then(() => next());
		}
		userInfoService.updateAppState(null, false, false);
		// Else if we're trying to access the maintenance page
		// when not in maintenance mode, or login while logged
		// redirect to home
	} else if (
		'maintenance' === to.name
		|| (
			(accessmasterManager.isLogged() || loginService.isLogged())
			&& ('accessmaster' === to.name || 'login' === to.name)
		)
	) {
		loadLang().then(() => next({ name: 'home' }));
	} else if (
		'patientExperienceSurvey' === to.name
		|| 'patientExperienceUnsubscribe' === to.name
	) {
		patientExperienceLoginService.checkAuthentication(to, from, next);
	} else if (
		'accessmaster' !== to.name
		&& 'login' !== to.name
		&& 'resetpassword' !== to.name
		&& 'createpassword' !== to.name
		&& 'projectSurveyCampaignSurveyRedirect' !== to.name
		&& !to.hash.includes('#access_token')
		&& !accessmasterManager.isLogged()
	) {
		// If the user is not logged, and not connecting/connected with accessmaster
		loginService.checkAuthentication(to, from, next);
	} else if (
		'accessmaster' === to.name
		|| to.hash.includes('#access_token')
		|| (accessmasterManager.isLogged() && !userInfoService.hasUserInfo())
	) {
		// If the user is connecting with accessmaster
		// or the token expired
		// or has not userinfo
		accessmasterManager.checkAuthentication(to, from, next);
	}

	if (
		loginService.isLogged()
		|| accessmasterManager.isLogged()
		|| 'accessmaster' === to.name
		|| 'loggedOut' === to.name
		|| 'resetpassword' === to.name
		|| 'createpassword' === to.name
		|| 'login' === to.name
		|| 'projectSurveyCampaignSurveyRedirect' === to.name
	) {
		loadLang().then(() => next());
	}
});

/** translate label and sublabel Route */
async function translateRoute(routes: RouteConfig[]) {
	await loadLang();

	routes.forEach((route: RouteConfig) => {
		if (undefined !== route.meta) {
			if (undefined !== route.meta.label) {
				route.meta.label = i18n.t(route.meta.label);
			}

			if (undefined !== route.meta.subLabel) {
				route.meta.subLabel = i18n.t(route.meta.subLabel);
			}
		}
	});
}

export default router;
